/**
 * FromCteRefBadge — a small "CTE" pill rendered on a FROM item that references
 * a CTE (Task cte-context, COM-05/06/08): a FROM item that names a CTE used to
 * render identically to a physical table, with nothing signalling it wasn't
 * one. Clicking the badge scrolls the matching `CteCard` definition into view
 * and briefly highlights it — the same scroll-to + highlight-by-data-attribute
 * mechanic §4b-ii's `NamedWindowRefNote.tsx` just landed for named-window
 * references (read as reference, not modified/shared — the CTE and named-
 * window definitions live in different sections of the render tree, so this
 * is a sibling implementation). `CteCard` already carries a `data-cte-name`
 * anchor (pre-existing, unrelated to this task); the highlight itself is a
 * dataset lookup (not a CSS attribute-selector string) so a quoted CTE name
 * containing special characters can't break the query.
 *
 * Fires `feature:vqe_cte_ref_focus` on activation — low-cardinality
 * `{ engine }` only, no CTE/table names (CLAUDE.md analytics props rule).
 */
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions } from '../../../../sql-grammar/model';
import { VqeTooltip } from '../common/VqeTooltip';

// Small structural labels, not app copy — vqeCopy.ts is owned by a held
// §4b-ii-fix lane this task avoids colliding with.
// TODO(vqe-copy): consolidate
const CTE_BADGE_LABEL = 'CTE';
const CTE_BADGE_TOOLTIP = "Jump to this CTE's definition";

export interface FromCteRefBadgeProps {
  /** The CTE's declared name (exact spelling `CteCard` wrote into `data-cte-name`). */
  readonly cteName: string;
  readonly options: ParseOptions;
  readonly testId: string;
}

function findCteCard(name: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const candidates = document.querySelectorAll<HTMLElement>('[data-cte-name]');
  for (const el of candidates) {
    if (el.dataset.cteName === name) return el;
  }
  return null;
}

export function FromCteRefBadge({ cteName, options, testId }: FromCteRefBadgeProps) {
  const { track } = useAnalytics();

  const focusDefinition = (): void => {
    track('feature:vqe_cte_ref_focus', { engine: options.engine });
    const target = findCteCard(cteName);
    if (!target) return;
    target.scrollIntoView({ block: 'nearest' });
    target.setAttribute('data-cte-ref-focus', 'true');
    window.setTimeout(() => target.removeAttribute('data-cte-ref-focus'), 1200);
  };

  return (
    <VqeTooltip content={CTE_BADGE_TOOLTIP}>
      <button
        type="button"
        onClick={focusDefinition}
        data-testid={testId}
        className="shrink-0 rounded bg-background px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground underline decoration-dotted hover:decoration-solid"
      >
        {CTE_BADGE_LABEL}
      </button>
    </VqeTooltip>
  );
}
